<template>
  <div class="tab-panel">
    <div class="bill-item">
      <div class="form-grid">
        <div class="form-group">
          <label>Title:</label>
          {{enrolledBill.title}}
        </div>
        <div class="form-group">
          <label>Short Title:</label>
          {{enrolledBill.short_title}}
        </div>
      </div>
      <div class="form-grid">
        <div class="form-group">
          <label>Bill Nos.:</label>
          {{enrolledBill.nos}}
        </div>
        <div class="form-group">
          <label>Authors:</label>
          {{formatArray(enrolledBill.authors)}}
        </div>
      </div>
      <div class="form-grid">
        <div class="form-group">
          <label>Remarks:</label>
          {{enrolledBill.remarks}}
        </div>
        <div class="form-group">
          <label>Source/Link:</label>
          <a v-if="enrolledBill.source" :href="enrolledBill.source" target="_blank" class="link">{{ enrolledBill.source }}</a>
        </div>
      </div>
      <div class="form-group">
        <label>Salient Features:</label>
        {{enrolledBill.salient_features}}
      </div>

      <!-- Legislative Status Section -->
      <div class="subsection">
        <div class="subsection-header">
          <h6>Legislative Status</h6>
        </div>
        <div v-for="(status, sidx) in enrolledBill.legisstatus" :key="sidx" class="status-item">
          <div class="status-header">
            <span>Status #{{ sidx + 1 }}</span>
          </div>
          <div class="form-grid">
            <div class="form-group">
              <label>Status:</label>
              {{status.status}}
            </div>
            <div class="form-group">
              <label>Date:</label>
              {{status.date}}
            </div>
          </div>
        </div>
      </div>

      <!-- Position Paper Section -->
      <div class="subsection">
        <div class="subsection-header">
          <h6>Position Paper</h6>
        </div>
        <div class="position-item">
          <div class="form-grid">
            <div class="form-group">
              <label>Position Paper Title:</label>
              {{enrolledBill.positionpaper.positiontitle}}
            </div>
            <div class="form-group">
              <label>Status:</label>
              {{ enrolledBill.positionpaper.status }}
            </div>
            <div class="form-group">
              <label>Date:</label>
              {{enrolledBill.positionpaper.date}}
            </div>
            <div class="form-group">
              <label>Source/Link:</label>
              <a v-if="enrolledBill.positionpaper.source" :href="enrolledBill.positionpaper.source" target="_blank" class="link">{{ enrolledBill.positionpaper.source }}</a>
            </div>
          </div>
        </div>
      </div>

      <!-- Enrollment Key Issues Section -->
      <KeyIssuesSection 
        :keyIssues="keyIssues"
        :activeTab="keyIssuesActive"
        :revenueStartYear="revenueStartYear"
        :revenueEndYear="revenueEndYear"
        :budgetaryStartYear="budgetaryStartYear"
        :budgetaryEndYear="budgetaryEndYear"
        @update:activeTab="keyIssuesActive = $event"
        @update:keyIssues="$emit('update:keyIssues', $event)"
        @addRevenueRow="$emit('addEnrollmentRevenueRow')"
        @addBudgetaryRow="$emit('addEnrollmentBudgetaryRow')"
        @addDofConcernsRow="$emit('addEnrollmentDofConcernsRow')"
        @deleteRevenueRow="$emit('deleteEnrollmentRevenueRow', $event)"
        @deleteBudgetaryRow="$emit('deleteEnrollmentBudgetaryRow', $event)"
        @deleteDofConcernsRow="$emit('deleteEnrollmentDofConcernsRow', $event)"
        @getYearTotal="$emit('getYearTotal', $event)"
        @autoResize="$emit('autoResize', $event)"
        @updateRevenueStartYear="$emit('updateRevenueStartYear', $event)"
        @updateRevenueEndYear="$emit('updateRevenueEndYear', $event)"
        @updateBudgetaryStartYear="$emit('updateBudgetaryStartYear', $event)"
        @updateBudgetaryEndYear="$emit('updateBudgetaryEndYear', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import KeyIssuesSection from './KeyIssuesSectionView.vue'

const props = defineProps({
  enrolledBill: {
    type: Object,
    required: true
  },
  keyIssues: {
    type: Object,
    required: true
  },
  authors: {
    type: Array,
    default: () => []
  },
  revenueStartYear: {
    type: Number,
    default: () => new Date().getFullYear()
  },
  revenueEndYear: {
    type: Number,
    default: () => new Date().getFullYear() + 5
  },
  budgetaryStartYear: {
    type: Number,
    default: () => new Date().getFullYear()
  },
  budgetaryEndYear: {
    type: Number,
    default: () => new Date().getFullYear() + 5
  },
  positionPapers: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits([
  'trackDeletedId',
  'addEnrollmentRevenueRow',
  'addEnrollmentBudgetaryRow',
  'addEnrollmentDofConcernsRow',
  'deleteEnrollmentRevenueRow',
  'deleteEnrollmentBudgetaryRow',
  'deleteEnrollmentDofConcernsRow',
  'getYearTotal',
  'autoResize',
  'addPositionPaper',
  'deletePositionPaper',
  'update:keyIssues',
  'updateRevenueStartYear',
  'updateRevenueEndYear',
  'updateBudgetaryStartYear',
  'updateBudgetaryEndYear'
])

const keyIssuesActive = ref('revenue')

const formatArray = (value) => {
  if (Array.isArray(value)) {
    return value.join(', ')
  }
  return value || '-'
}
</script>

<style scoped>
.bill-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #ea580c;
  font-size: 0.9rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.3s;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #ea580c;
  box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
}

.subsection {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}

.subsection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.subsection-header h6 {
  color: #ea580c;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
}

.status-item, .position-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.status-header, .position-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.add-sub-btn {
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
}

.add-sub-btn:hover {
  background: linear-gradient(135deg, #c2410c 0%, #ea580c 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(234, 88, 12, 0.4);
}

.delete-sub-btn {
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.8rem;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
}

.delete-sub-btn:hover {
  background: linear-gradient(135deg, #b91c1c 0%, #dc2626 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
}

.link {
  word-wrap: break-word;
  word-break: break-all;
  overflow-wrap: break-word;
  hyphens: auto;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .subsection-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .status-header, .position-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>